import React from 'react';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import useJsonLd from '../useJsonLd';
import { contactLink } from '../topics';
import { useSiteContent } from '../SiteContent';
import T from '../components/EditableText';
import FaqSection from '../components/FaqSection';
import CtaBand from '../components/CtaBand';

/**
 * Häufige Fragen zu den Kosten.
 *
 * Speist sowohl den sichtbaren Abschnitt als auch das FAQ-Markup für Google —
 * bewusst eine gemeinsame Quelle, denn strukturierte Daten ohne entsprechenden
 * Seiteninhalt verstoßen gegen Googles Richtlinien. Weil die Fragen und Antworten
 * seit der Umstellung vom Kunden bearbeitet werden können, zieht das Markup
 * automatisch mit. Jede Antwort stammt aus den Angaben weiter oben auf dieser
 * Seite; wer dort Preise ändert, muss hier mit.
 */
const FAQ_NUMBERS = [1, 2, 3, 4, 5, 6, 7];

/** Zeilen der Vergleichstabelle; Texte `kosten.vergleich.zeile-N.*`. */
const VERGLEICH_ZEILEN = [1, 2, 3, 4, 5];

/**
 * Posten der Beispielrechnung; Texte `kosten.beispiel.posten-N` / `.wert-N`.
 *
 * Die 3 fehlt: Dort stand der Entlastungsbetrag, der für die 24-Stunden-
 * Betreuung nicht abgerechnet werden kann (siehe Kommentar in `kosten.ts`).
 * Die übrigen Posten behalten ihre Nummer, damit ein vom Kunden in der
 * Verwaltung geänderter Text an seinem Posten bleibt und nicht um eine
 * Zeile verrutscht.
 */
const BEISPIEL_POSTEN = [1, 2, 4];

const Kosten: React.FC = () => {
  const { plain } = useSiteContent();

  // Titel und Beschreibung für die Suchergebnisse stehen bewusst weiterhin im
  // Code: Sie sind auf die Auswertung der Search Console hin geschnitten und
  // gehören nicht zu dem, was nebenbei umformuliert werden sollte.
  usePageMeta(
    'Kosten der Seniorenbetreuung in Gifhorn | Preise 2026',
    'Alle Preise auf einen Blick: Hauswirtschaft ab 37 €/Std., 24-Stunden-Betreuung ab 100 €/Tag. Dazu Entlastungsbetrag, Pflegegeld und Verhinderungspflege.',
    { standaloneTitle: true },
  );

  const faqJsonLd = React.useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_NUMBERS.map((number) => ({
        '@type': 'Question',
        name: plain(`kosten.faq.${number}.frage`),
        acceptedAnswer: { '@type': 'Answer', text: plain(`kosten.faq.${number}.antwort`) },
      })),
    }),
    [plain],
  );

  useJsonLd('kosten-faq', faqJsonLd);

  return (
    <>
      {/* Kopf ohne Foto: links die Frage, rechts die Preise auf einen Blick */}
      <section className="shell hx-hero-wrap">
        <div className="hx-hero hx-hero--text">
          <div className="hx-hero__body">
            <span className="eyebrow eyebrow--on-image">
              <T k="kosten.hero.eyebrow" />
            </span>
            {/* Als Frage formuliert, weil genau so danach gesucht wird —
                der Versprechensteil steht im Absatz darunter. */}
            <h1 className="h-page hx-hero__title">
              <T k="kosten.hero.titel" />{' '}
              <span className="accent">
                <T k="kosten.hero.titel-akzent" />
              </span>
            </h1>
            <p className="hx-hero__lead">
              <T k="kosten.hero.lead" />
            </p>
          </div>
          <div className="hx-hero__aside">
            <div className="hx-pick hx-pick--prices">
              <div className="hx-pick__title"><T k="kosten.kopf.karte" /></div>
              <dl>
                <div><dt><T k="kosten.hauswirtschaft.titel" /></dt><dd><T k="kosten.hauswirtschaft.preis-1" /></dd></div>
                <div><dt><T k="kosten.alltagsbetreuung.titel" /></dt><dd><T k="kosten.alltagsbetreuung.preis-1" /></dd></div>
                <div><dt><T k="kosten.24h.titel" /> · <T k="kosten.24h.posten-1" /></dt><dd><T k="kosten.24h.preis-1" /></dd></div>
              </dl>
              <a href="#beispiel" className="textlink"><T k="kosten.kopf.beispiel" /> <Icon name="arrow-right" size={16} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* Stundensätze als drei Spalten */}
      <section className="shell section">
        <div className="hx-steps__head">
          <h2 className="h-section"><T k="kosten.leistungen.titel" /></h2>
          <p><T k="kosten.leistungen.einleitung" /></p>
        </div>
        <div className="hx-tiers">
          <article className="hx-tier">
            <span className="hx-tier__icon"><Icon name="sparkles" size={20} /></span>
            <h3 className="h-card"><T k="kosten.hauswirtschaft.titel" /></h3>
            <div className="hx-tier__price"><T k="kosten.hauswirtschaft.preis-1" /></div>
            <div className="hx-tier__unit"><T k="kosten.hauswirtschaft.posten-1" /></div>
            <dl className="hx-tier__rows">
              <div>
                <dt><T k="kosten.hauswirtschaft.posten-2" /></dt>
                <dd><T k="kosten.hauswirtschaft.preis-2" /></dd>
              </div>
            </dl>
          </article>
          <article className="hx-tier">
            <span className="hx-tier__icon"><Icon name="hand-heart" size={20} /></span>
            <h3 className="h-card"><T k="kosten.alltagsbetreuung.titel" /></h3>
            <div className="hx-tier__price"><T k="kosten.alltagsbetreuung.preis-1" /></div>
            <div className="hx-tier__unit"><T k="kosten.alltagsbetreuung.posten-1" /></div>
            <dl className="hx-tier__rows">
              <div>
                <dt><T k="kosten.alltagsbetreuung.posten-2" /></dt>
                <dd><T k="kosten.alltagsbetreuung.preis-2" /></dd>
              </div>
            </dl>
          </article>
          <article className="hx-tier hx-tier--dark">
            <span className="hx-tier__icon"><Icon name="clock" size={20} /></span>
            <h3 className="h-card"><T k="kosten.24h.titel" /></h3>
            <div className="hx-tier__price"><T k="kosten.24h.preis-1" /></div>
            <div className="hx-tier__unit"><T k="kosten.24h.posten-1" /></div>
            <dl className="hx-tier__rows">
              <div>
                <dt><T k="kosten.24h.posten-2" /></dt>
                <dd><T k="kosten.24h.preis-2" /></dd>
              </div>
              <div>
                <dt><T k="kosten.24h.posten-3" /></dt>
                <dd><T k="kosten.24h.preis-3" /></dd>
              </div>
              <div>
                <dt><T k="kosten.24h.posten-4" /></dt>
                
              </div>
            </dl>
          </article>
        </div>

        {/* Finanzierung */}
        <div className="hx-fund">
          <div>
            <span className="hx-note__mark"><Icon name="piggy-bank" size={18} /> <T k="kosten.finanzierung.titel" /></span>
            <p><T k="kosten.finanzierung.eigenanteil" /></p>
          </div>
          <ul className="hx-checks hx-checks--one">
            {[1, 2, 3].map((n) => (
              <li key={n}><Icon name="check" size={17} /><span><T k={`kosten.finanzierung.punkt-${n}`} /></span></li>
            ))}
          </ul>
        </div>
        <p className="note" style={{ marginTop: 14 }}>
          <T k="kosten.finanzierung.abrechnung" />
        </p>
      </section>

      {/* Vergleich Heim / ambulant / 24h — die Frage, die Angehörige sich
          wirklich stellen. Die Zahlen sind Durchschnittswerte mit Quelle;
          siehe Kommentar in content/kosten.ts. */}
      <section className="shell" id="vergleich" style={{ paddingTop: 46, paddingBottom: 10 }}>
        <h2 className="h-section">
          <T k="kosten.vergleich.titel" />
        </h2>
        <p className="hx-muted-lead" style={{ maxWidth: 680, marginBottom: 26 }}>
          <T k="kosten.vergleich.einleitung" />
        </p>
        <div className="compare-wrap">
          <table className="compare">
            <thead>
              <tr>
                <th scope="col"><T k="kosten.vergleich.spalte-merkmal" /></th>
                <th scope="col"><T k="kosten.vergleich.spalte-heim" /></th>
                <th scope="col"><T k="kosten.vergleich.spalte-ambulant" /></th>
                <th scope="col" className="compare__own"><T k="kosten.vergleich.spalte-24h" /></th>
              </tr>
            </thead>
            <tbody>
              {VERGLEICH_ZEILEN.map((zeile) => (
                <tr key={zeile}>
                  <th scope="row"><T k={`kosten.vergleich.zeile-${zeile}.merkmal`} /></th>
                  <td><T k={`kosten.vergleich.zeile-${zeile}.heim`} /></td>
                  <td><T k={`kosten.vergleich.zeile-${zeile}.ambulant`} /></td>
                  <td className="compare__own"><T k={`kosten.vergleich.zeile-${zeile}.24h`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="note" style={{ marginTop: 14, maxWidth: 820 }}>
          <T k="kosten.vergleich.quelle" />
        </p>
      </section>

      {/* Beispielrechnung */}
      <section className="shell section hx-twocol hx-example" id="beispiel">
        <div>
          <h2 className="h-section">
            <T k="kosten.beispiel.titel" />
          </h2>
          <p className="hx-muted-lead">
            <T k="kosten.beispiel.einleitung" />
          </p>
          <h3 className="h-card">
            <T k="kosten.tagessatz.titel" />
          </h3>
          <p className="hx-muted-lead">
            <T k="kosten.tagessatz.text" />
          </p>
        </div>
        <div className="hx-example__card">
          <dl className="calc">
            {BEISPIEL_POSTEN.map((posten) => (
              <div className="calc__row" key={posten}>
                <dt><T k={`kosten.beispiel.posten-${posten}`} /></dt>
                <dd><T k={`kosten.beispiel.wert-${posten}`} /></dd>
              </div>
            ))}
            <div className="calc__row calc__sum">
              <dt><T k="kosten.beispiel.summe" /></dt>
              <dd><T k="kosten.beispiel.summe-wert" /></dd>
            </div>
          </dl>
          <p className="note" style={{ marginTop: 14 }}>
            <T k="kosten.beispiel.hinweis" />
          </p>
        </div>
      </section>

      <FaqSection titleKey="kosten.faq.titel" numbers={FAQ_NUMBERS} keyOf={(n) => `kosten.faq.${n}`} />

      <CtaBand titleKey="kosten.angebot.titel" textKey="kosten.angebot.text" ctaKey="kosten.angebot.cta" to={contactLink('CONTACT')} />
      <div style={{ height: 48 }} />
    </>
  );
};

export default Kosten;
